import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'

import { normalizeApiError } from './apiEnvelope'

/**
 * Aplica os erros de campo vindos da API (RFC7807 `errors`) ao formulário
 * react-hook-form e devolve a mensagem geral para exibição no topo.
 *
 * Com `fields` (os campos visíveis na tela), só esses recebem o erro: mensagens
 * de chaves sem campo correspondente vão para o aviso geral, e quando todas
 * couberem nos campos não há aviso geral (o `detail` só repetiria as mesmas
 * mensagens). Sem `fields`, mantém o comportamento antigo: tudo vai para o form
 * e o aviso geral é o `detail`.
 *
 * `onUnmatched` permite tratar uma chave sem campo no form (ex.: `certificado.nomeArquivo`,
 * exibida num componente de arquivo): devolvendo `true`, ela não vai para o aviso geral.
 */
export function applyApiErrorToForm<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields?: readonly Path<T>[],
  onUnmatched?: (field: string, message: string) => boolean,
): string | null {
  const normalized = normalizeApiError(error)
  const entries = Object.entries(normalized.fieldErrors)

  if (!fields) {
    for (const [field, message] of entries) {
      setError(field as Path<T>, { type: 'server', message })
    }
    return normalized.detail
  }

  const unmatched: string[] = []
  for (const [field, message] of entries) {
    if (fields.includes(field as Path<T>)) {
      setError(field as Path<T>, { type: 'server', message })
    } else if (!onUnmatched?.(field, message)) {
      unmatched.push(message)
    }
  }

  if (unmatched.length > 0) return unmatched.join(' ')
  return entries.length > 0 ? null : normalized.detail
}
